<template>
  <pno-loading :loading="loading">

    <!-- Map overlay -->
    <div ref="modal" class="modal fade" id="mapModal" tabindex="-1" role="dialog" aria-labelledby="mapModalTitle" aria-hidden="true" v-if="location">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title" id="mapModalTitle">{{ location.name }}<br>
              <small class="small grey">{{ location.address }}, {{ location.country }}</small>
            </h6>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true"><i class="fal fa-times-circle"></i></span>
            </button>
          </div>
          <div class="modal-body">
            <l-map
            ref="map"
            :zoom="13"
            :center="coordinates"
            style="min-height: 300px"
            >
              <l-tile-layer :url="tileProvider.osm.url" :attribution="tileProvider.osm.attribution"></l-tile-layer>
              <l-marker :lat-lng="coordinates"></l-marker>
            </l-map>
          </div>
        </div>
      </div>
    </div>

    <div class="container py-2">
      <br>
      <div class="row" v-if="!loading && trailer">
        <div class="col-md-7 col-lg-8">
          <h2 class="small grey text-uppercase mt-1 mb-2">{{ trailerCategoryName(trailer.details.category) }} Trailer for rent:</h2>
          <h1 class="h4"><span class="text-uppercase">{{ trailer.details.manufacturer}}</span> {{ trailer.details.name }}<small class="grey ml-2">({{ trailer.details.modelYear }})</small></h1>
          <p class="small">
            <i class="fal fa-map-marker-alt mx-1"></i> Parked in <a v-if="location" href="#location" @click.prevent="refreshMap" data-toggle="modal" data-target="#mapModal">{{ location.name }} ({{ location.city }})</a><b v-else>N/A</b>
          </p>
          <smart-img
            preset="format-32-lg"
            :src="trailerTypeImageUrl(trailer)"
            class="img-fluid mb-1"
            alt=""
          />
          <p class="small"><small>Photo represents PNO's most commonly rented <span class="text-lowercase">{{ trailerCategoryName(trailer.details.category) }}</span> trailer.</small></p>
          <p class="lead grey">{{ trailerCategoryDescription(trailer.details.category) }}</p>
          <hr>
          <div class="row">
            <div class="col-md-7">
              <h3 class="h6">Specifications:</h3>
              <dl class="row trailer-details">
                <dt class="col-7">Manufacturer</dt>
                <dd class="col-5 text-right text-uppercase">{{ trailer.details.manufacturer }}</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-3">Model</dt>
                <dd class="col-9 text-right text-uppercase">{{ trailer.details.name }}</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Year</dt>
                <dd class="col-5 text-right">{{ trailer.details.modelYear }}</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-4">Features</dt>
                <dd class="col-8 text-right">
                  &nbsp;
                  <span v-for="feature in trailer.details.features" :key="feature" class="badge badge-light text-uppercase font-weight-normal ml-1">{{ trailerFeatureName(feature) }} </span>
                </dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Maximum load</dt>
                <dd class="col-5 text-right">{{ trailer.details.maximumLoad | Number }} kg</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Total weight</dt>
                <dd class="col-5 text-right">{{ trailer.details.totalWeight | Number }} kg</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">King pin pressure</dt>
                <dd class="col-5 text-right">{{ trailer.details.kingPinPressure | Number }} kg</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Curb weight</dt>
                <dd class="col-5 text-right">{{ trailer.details.curbWeight | Number }} kg</dd>
              </dl>

              <dl class="row trailer-details d-none">
                <dt class="col-7">Reg. nr.</dt>
                <dd class="col-5 text-right">{{ trailer.details.registrationNumber }}</dd>
              </dl>
              <dl class="row trailer-details d-none">
                <dt class="col-5">Chassis nr.</dt>
                <dd class="col-7 text-right">{{ trailer.details.vin }}</dd>
              </dl>
            </div>
            <div class="col-md-5">
              <h3 class="h6">Dimensions:</h3>
              <dl class="row trailer-details">
                <dt class="col-7">Axles</dt>
                <dd class="col-5 text-right">{{ trailer.details.numberOfAxles }}</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-5">Tires</dt>
                <dd class="col-7 text-right">&nbsp;{{ trailer.details.tyreDimensions }}</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">External height</dt>
                <dd class="col-5 text-right">{{ trailer.details.externalHeight | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">External length</dt>
                <dd class="col-5 text-right">{{ trailer.details.externalLength | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">External width</dt>
                <dd class="col-5 text-right">{{ trailer.details.externalWidth | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Internal height</dt>
                <dd class="col-5 text-right">{{ trailer.details.internalHeight | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Internal length</dt>
                <dd class="col-5 text-right">{{ trailer.details.internalLength | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Internal width</dt>
                <dd class="col-5 text-right">{{ trailer.details.internalWidth | Number }} mm</dd>
              </dl>
              <dl class="row trailer-details">
                <dt class="col-7">Interconnection height</dt>
                <dd class="col-5 text-right">{{ trailer.details.interconnectionHeight | Number }} mm</dd>
              </dl>
            </div>
          </div>
          <div class="card mt-3">
            <div class="card-body">
              <h3 class="h6 mb-3">Rental details</h3>
              <div class="row small">
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> No upfront payment
                </div>
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> From 30 days
                </div>
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> Up to 12 months
                </div>
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> All-inclusive price
                </div>
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> Insurance
                </div>
                <div class="col-md-4 mb-1">
                  <i class="fas fa-check green mr-1"></i> Roadside assistance
                </div>
              </div>
              <hr>
              <div class="row small">
                <div class="col-md-4" v-if="location">
                  <h2 class="h6">Location</h2>
                  <p class="grey">
                    {{ location.name }}<br>
                    {{ location.address }}<br>
                    {{ location.country }}
                  </p>
                  <p class="mb-0"><i class="fas fa-map-marked-alt mr-1"></i> <a
                      href="#googlemapslink"
                      target="_blank"
                      class="black"
                      data-toggle="modal"
                      data-target="#mapModal"
                      @click.prevent="refreshMap"
                    >View map</a></p>
                </div>

                <div class="col-md-8 col-lg-6 col-xl-5 text-center text-sm-left">
                  <hr class="d-md-none">
                  <h2 class="h6">Questions?</h2>
                  <p>Get in touch to customize your trailer. We specify, order and deliver for you.</p>
                  <p class="h6 mb-3" v-if="location"><i class="fa fa-phone mr-2"></i>{{ phoneNumber }}</p>
                </div>

              </div>
            </div>
          </div>
          <br>
        </div>

        <div class="col-md-5 col-lg-4">
          <div class="card mb-2 sticky">
            <div class="card-header">
              <small>
                <span v-if="!availableFrom">
                  <i class="far fa-calendar-exclamation mr-1"></i> <strong>Coming soon</strong>. Request pickup date.
                </span>
                <span v-else>
                  <i class="far fa-calendar-check mr-1"></i> Available from <strong>{{ availableFrom }}</strong>.
                </span>
              </small>
            </div>
            <div class="card-body">
              <form class="">
                <div class="form-group mb-3">
                  <label class="small grey text-uppercase">Pickup</label>
                  <!-- TODO: if traileronworkshop don't auto fillout from date -->
                  <date-picker updateOnScroll :disabled="isQuoted(trailer.assetId)" class="input-group date" v-model="pickupDate" :minDate="trailer.availableFrom" :validate="validatePickupDate" />
                </div>
                <div class="form-group mb-1">
                  <label class="small grey text-uppercase">Return</label>
                  <date-picker updateOnScroll :disabled="isQuoted(trailer.assetId)" class="input-group date" v-model="returnDate" :minDate="minReturnDate" :maxDate="maxReturnDate" :validate="validateReturnDate"/>
                </div>
                <span class="d-block mb-2"></span>
                <table class="table pricing-trailer mb-1">
                  <tbody>
                    <tr>
                      <td class="align-bottom border-0">
                        <span class="small grey text-uppercase d-block">Length</span>
                        {{ durationDays }} days
                      </td>
                    </tr>
                  </tbody>
                </table>

                <p class="mb-2">
                  <button :disabled="!quoteRequestIsValid" v-if="!isQuoted(trailer.assetId)" type="button" class="btn btn-lg btn-block btn-success" data-toogle="tooltip" title="Add to Quote" v-on:click="addToQuote(trailer.assetId, pickupDate, returnDate)"><i class="far fa-plus-square mr-1"></i> Add to Quote</button>
                  <button v-else type="button" class="btn btn-lg btn-block btn-warning" data-toogle="tooltip" title="Remove from Quote" v-on:click="removeFromQuote(trailer.assetId)"><i class="far fa-minus-square mr-1"></i> Remove from Quote</button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </pno-loading>
</template>

<script>
import Vue from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import Number from '@/filters/number.js'

export default {
  filters: { Number },
  async mounted() {
    this.$store.commit('meta/title', 'PNO Trailer Rental')
    const assetId = this.$route.params.id
    this.loading = true
    this.trailer = await this.$store.dispatch('assets/getAsset', assetId)
    this.loading = false
    if (!this.trailer) {
      this.$_error(require('@/views/TrailerNotFound').default, { assetId: assetId })
      return
    }
    if (this.isQuoted(assetId)) {
      this.pickupDate = this.$moment.utc(this.quoteItems[assetId].pickupDate).format(this.formDateFormat)
      this.returnDate = this.$moment.utc(this.quoteItems[assetId].returnDate).format(this.formDateFormat)
    }
    else if (this.trailer.availableFrom) {
      this.pickupDate = this.$moment(this.trailer.availableFrom).format(this.formDateFormat)
      this.returnDate = ''
    } else {
      this.pickupDate = ''
      this.returnDate = ''
    }
    const brandtypeyear = this.trailer.details.manufacturer.capitalize() + " " + this.trailerCategoryName(this.trailer.details.category) + " (" + this.trailer.details.modelYear + ")";
    const citycountry = this.location.city + ", " + this.location.country;

    this.$store.commit('meta/title', "Rent a " + brandtypeyear + " trailer in " + citycountry)
    this.$store.commit('meta/description', "Flexible short-term trailer rental of " + brandtypeyear + " with " + this.trailer.details.features.join(', ') + " parked in " + citycountry + ".");
    this.$store.commit('meta/image', this.generateSmartImgUrl(this.trailerTypeImageUrl(this.trailer), 'ogimage'));
  },
  data: () => ({
    formDateFormat: 'DD/MM-YYYY',
    loading: true,
    trailer: null,
    pickupDate: '',
    returnDate: '',
    tileProvider: {
      osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      },
      esri: {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      }
    }
  }),
  methods: {
    validatePickupDate() {
      const pickupDate = this.pickupDate ? this.$moment.utc(this.pickupDate, this.formDateFormat) : null
      if (!pickupDate) return null

      if (!pickupDate.isValid()) {
        return "Please enter a valid date (dd/mm-yyyy)"
      }

      const availableFrom = this.$moment(this.trailer.availableFrom)
      if (availableFrom.isValid()) {
        if (pickupDate.isBefore(availableFrom)) {
          return "Trailer is not available before " + availableFrom.format('DD/MM-YYYY')
        }
      }

      return null
    },
    validateReturnDate() {
      const returnDate = this.returnDate ? this.$moment.utc(this.returnDate, this.formDateFormat) : null
      if (!returnDate) return null

      if (!returnDate.isValid()) {
        return "Please enter a valid date (dd/mm-yyyy)"
      }

      const pickupDate = this.pickupDate ? this.$moment.utc(this.pickupDate, this.formDateFormat) : null
      if (!pickupDate) return null

      if (pickupDate.isValid()) {
        const minReturnDate = this.$moment(this.minReturnDate)
        const maxReturnDate = this.$moment(this.maxReturnDate)
        if (returnDate.isBefore(pickupDate)) {
          return "Trailer can not be returned before pickup"
        }
        if (returnDate.isBefore(minReturnDate)) {
          return "Trailer can not be rented less than 30 days"
        }
        if (returnDate.isAfter(maxReturnDate)) {
          return "Trailer can not be rented for more than 365 days"
        }
      }

      return null
    },
    refreshMap() {
      // @hack No proper event map loaded
      const self = this
      this.$refs.map.mapObject._onResize()
      setTimeout(function () {
        self.$refs.map.mapObject.invalidateSize()
      }, 200)
      setTimeout(function () {
        self.$refs.map.mapObject.invalidateSize()
      }, 2000)
    },
    addToQuote(assetId, inputPickupDate, inputReturnDate) {
      const pickupDate = this.$moment.utc(inputPickupDate, this.formDateFormat)
      const returnDate = this.$moment.utc(inputReturnDate, this.formDateFormat)
      this.$store.dispatch('quote/addToQuote', { assetId, pickupDate, returnDate })
      return false
    },
    removeFromQuote(assetId) {
      this.$store.dispatch('quote/removeFromQuote', assetId)
      return false
    },
  },
  computed: {
    ...mapState('user', ['isLoggedIn']),
    ...mapGetters('quote', ['isQuoted']),
    ...mapState('app', ['loginUrl']),
    ...mapState('locations', ['locations']),
    quoteRequestIsValid() {
      if (!this.returnDate) return false
      const pickupDate = this.$moment.utc(this.pickupDate, 'DD/MM-YYYY')
      const returnDate = this.$moment.utc(this.returnDate, 'DD/MM-YYYY')
      if (!pickupDate.isValid() || !returnDate.isValid()) return false
      if (!returnDate.isAfter(pickupDate)) return false
      return true
    },
    totalPriceGross() {
        return this.durationDays ? this.totalPrice + this.totalDiscount : 0
    },
    totalPrice() {
      return this.durationDays ? this.durationDays*19 : 0
    },
    totalDiscount() {
      return this.durationDays ? this.durationDays*4 : 0
    },
    currentPickupDate() {
      const pickupDate = this.pickupDate ? this.$moment.utc(this.pickupDate, 'DD/MM-YYYY') : null
      return pickupDate && pickupDate.isValid() ? pickupDate : null
    },
    minReturnDate() {
      // subtract one to get return day included in calculation
      return this.currentPickupDate ? this.currentPickupDate.clone().add(30 - 1, 'days').format() : null
    },
    maxReturnDate() {
      // subtract one to get return day included in calculation
      return this.currentPickupDate ? this.currentPickupDate.clone().add(365 - 1, 'days').format() : null
    },
    availableFrom() {
      if (!this.trailer.availableFrom) {
        return null
      }

      return this.$moment(this.trailer.availableFrom).format('DD/MM-YYYY')
    },
    quoteItems() {
      return this.$store.state.quote.items
    },
    location() {
      return this.trailer && this.locations[this.trailer.depotSlug] ? this.locations[this.trailer.depotSlug] : null
    },
    phoneNumber() {
      const countryCode = this.locations[this.trailer.depotSlug] && this.locations[this.trailer.depotSlug].countryCode

      const country = Object.values(this.locations).filter(function(location) {
        return location.isHeader && location.countryCode == countryCode && location.countryCode
      })

      return country && country.shift().phoneNumber
    },
    coordinates() {
      if (this.location) {
        return [this.location.lat, this.location.long]
      }
      else {
        // 0, 0 resolves to undef, undef somewhere along the leaflet chain ...
        return [0.1, 0.1]
      }
    },
    durationDays() {
      return this.$store.getters['quote/durationDays'](this.$moment.utc(this.pickupDate, this.formDateFormat), this.$moment.utc(this.returnDate, this.formDateFormat))
    }
  },
};
</script>
